import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Pencil, RefreshCw, Trash2, X } from 'lucide-react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, getApiErrorMessage } from '../lib/api'
import { asArray, asCollection } from '../lib/collections'
import { formatDateTime } from '../lib/date'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'

const EVENT_TYPE_LABELS = {
  NOTIFICATION: '알림',
  FAX: '팩스',
  AI_TEXT: 'AI 텍스트',
  AI_IMAGE: 'AI 이미지',
  DOCUMENT_OCR: '문서 OCR',
}

const UNIT_LABELS = {
  MESSAGE: '건',
  PAGE: '페이지',
  TOKEN: '토큰',
  IMAGE: '이미지',
}

const STATUS_LABELS = {
  SUCCESS: '성공',
  SUCCEEDED: '성공',
  SUBMITTED: '접수',
  DELIVERED: '전송완료',
  FAILED: '실패',
  FAIL: '실패',
  CANCELLED: '취소',
  CANCELED: '취소',
  REQUESTED: '요청',
  UNKNOWN: '확인필요',
}

const EVENT_TYPE_OPTIONS = [
  { value: '', label: '전체 비용' },
  { value: 'NOTIFICATION', label: '알림' },
  { value: 'FAX', label: '팩스' },
  { value: 'AI_TEXT', label: 'AI 텍스트' },
  { value: 'AI_IMAGE', label: 'AI 이미지' },
  { value: 'DOCUMENT_OCR', label: '문서 OCR' },
]

const UNIT_OPTIONS = [
  { value: 'MESSAGE', label: '메시지 1건' },
  { value: 'PAGE', label: '문서/팩스 1페이지' },
  { value: 'TOKEN', label: 'AI 토큰 1개' },
  { value: 'IMAGE', label: 'AI 이미지 1장' },
]

const CATEGORY_COLORS = ['#0d9488', '#2563eb', '#f97316', '#7c3aed', '#16a34a', '#dc2626']
const STATIC_FEATURE_CATEGORIES = ['보장분석']
const EMPTY_RATE_FORM = {
  eventType: 'NOTIFICATION',
  provider: 'POPBILL',
  serviceName: '알림톡',
  unitType: 'MESSAGE',
  unitPrice: '',
  currency: 'KRW',
  modelName: '',
  memo: '',
  active: true,
}

const RATE_POLICY_PRESETS = [
  { eventType: 'NOTIFICATION', provider: 'POPBILL', serviceName: '알림톡', unitType: 'MESSAGE' },
  { eventType: 'NOTIFICATION', provider: 'POPBILL', serviceName: 'SMS', unitType: 'MESSAGE' },
  { eventType: 'NOTIFICATION', provider: 'POPBILL', serviceName: 'LMS', unitType: 'MESSAGE' },
  { eventType: 'NOTIFICATION', provider: 'POPBILL', serviceName: '문자', unitType: 'MESSAGE' },
  { eventType: 'NOTIFICATION', provider: 'SOLAPI', serviceName: '알림톡', unitType: 'MESSAGE' },
  { eventType: 'NOTIFICATION', provider: 'REST_SMS', serviceName: '문자', unitType: 'MESSAGE' },
  { eventType: 'FAX', provider: 'POPBILL', serviceName: '팩스', unitType: 'PAGE' },
  { eventType: 'DOCUMENT_OCR', provider: 'UPSTAGE', serviceName: 'Upstage 문서 처리', unitType: 'PAGE', modelName: 'document-parse' },
  { eventType: 'AI_TEXT', provider: 'OPENAI', serviceName: 'AI 텍스트', unitType: 'TOKEN', modelName: 'gpt-4o-mini' },
  { eventType: 'AI_TEXT', provider: 'OPENAI', serviceName: 'AI 텍스트', unitType: 'TOKEN', modelName: 'gpt-5.4-mini' },
  { eventType: 'AI_TEXT', provider: 'OPENAI', serviceName: 'AI 텍스트', unitType: 'TOKEN', modelName: 'gpt-4o' },
  { eventType: 'AI_TEXT', provider: 'GEMINI', serviceName: 'AI 텍스트', unitType: 'TOKEN', modelName: 'gemini-2.5-flash' },
  { eventType: 'AI_TEXT', provider: 'GEMINI', serviceName: 'AI 텍스트', unitType: 'TOKEN', modelName: 'gemini-2.5-flash-lite' },
  { eventType: 'AI_IMAGE', provider: 'OPENAI', serviceName: 'AI 이미지', unitType: 'IMAGE', modelName: 'gpt-image-2' },
  { eventType: 'AI_IMAGE', provider: 'GEMINI', serviceName: 'AI 이미지', unitType: 'IMAGE', modelName: 'gemini-3.1-flash-image' },
  { eventType: 'AI_IMAGE', provider: 'GEMINI', serviceName: 'AI 이미지', unitType: 'IMAGE', modelName: 'gemini-2.5-flash-image' },
  { eventType: 'AI_IMAGE', provider: 'GEMINI', serviceName: 'AI 이미지', unitType: 'IMAGE', modelName: 'gemini-3-pro-image' },
]

const REF_TYPE_CATEGORY_LABELS = {
  PROFILE_CARD_PHOTO: '사용자 프로필',
  PROFILE_REPORT_PHOTO: '사용자 프로필',
  MESSAGE_TEMPLATE_TEXT: '메시지 템플릿',
  SALES_IMAGE_PROMPT: '이미지자료실',
  INSURANCE_NEWS_DIGEST: '보험뉴스',
  INSURER_NEWSLETTER_SUMMARY: '보험사 소식지',
  INSURER_NEWSLETTER_PROMPT: '보험사 소식지',
  MONTHLY_INSURANCE_BRIEF: '보험사 소식지',
  CUSTOMER_BUSINESS_CARD_OCR: '고객관리',
  CUSTOMER_COVERAGE_ANALYSIS_OCR: '보장분석',
  CUSTOMER_COVERAGE_ANALYSIS_PARSE: '보장분석',
}

const REF_TYPE_FEATURE_LABELS = {
  PROFILE_CARD_PHOTO: '명함용 사진 배경 제거',
  PROFILE_REPORT_PHOTO: '리포트용 사진 배경 제거',
  CUSTOMER_COVERAGE_ANALYSIS_OCR: '보장분석 PDF OCR',
  CUSTOMER_COVERAGE_ANALYSIS_PARSE: '보장분석 PDF 문서 파싱',
}

function unwrapData(res) {
  const p = res?.data
  if (p?.data !== undefined) return p.data
  return p ?? null
}

function num(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('ko-KR')
}

function money(n, currency = 'KRW') {
  if (n == null || Number.isNaN(Number(n))) return '단가 없음'
  const value = Number(n)
  if (currency === 'KRW') {
    return `${value.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}원`
  }
  return `${value.toLocaleString('ko-KR', { maximumFractionDigits: 4 })} ${currency || ''}`.trim()
}

function unitMoney(n, currency = 'KRW') {
  if (n == null || Number.isNaN(Number(n))) return '단가 없음'
  const value = Number(n)
  const formatted = value.toLocaleString('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })
  if (currency === 'KRW') {
    return `${formatted}원`
  }
  return `${formatted} ${currency || ''}`.trim()
}

function eventTypeLabel(v) {
  return EVENT_TYPE_LABELS[v] || v || '—'
}

function unitLabel(v) {
  return UNIT_LABELS[v] || v || '—'
}

function statusLabel(v) {
  if (!v) return '—'
  return STATUS_LABELS[String(v).toUpperCase()] || v
}

function normalizeKey(v) {
  return String(v || '').trim().toUpperCase()
}

function inferFeatureCategory(row) {
  if (!row || typeof row !== 'object') return row || '미분류'
  if (row.featureCategory) return row.featureCategory

  const refType = normalizeKey(row.refType)
  const eventType = normalizeKey(row.eventType)
  const featureName = String(row.featureName || '')

  if (REF_TYPE_CATEGORY_LABELS[refType]) return REF_TYPE_CATEGORY_LABELS[refType]
  if (eventType === 'FAX') return '보험금청구'
  if (eventType === 'NOTIFICATION') return '커뮤니케이션'
  if (refType.includes('COVERAGE_ANALYSIS') || featureName.includes('보장분석')) return '보장분석'
  if (eventType === 'DOCUMENT_OCR') return '문서/이미지 인식'
  if (eventType === 'AI_IMAGE' || refType.includes('IMAGE') || featureName.includes('이미지')) return '이미지자료실'
  if (refType.includes('BUSINESS_CARD') || featureName.includes('명함')) return '고객관리'
  if (refType.includes('NEWS_DIGEST') || featureName.includes('보험뉴스')) return '보험뉴스'
  if (refType.includes('NEWSLETTER') || featureName.includes('소식지')) return '보험사 소식지'
  if (refType.includes('TEMPLATE') || featureName.includes('템플릿')) return '메시지 템플릿'
  if (eventType === 'AI_TEXT') return 'AI 공통'
  return '미분류'
}

function featureCategoryLabel(v) {
  if (v && typeof v === 'object') return inferFeatureCategory(v)
  return v || '미분류'
}

function featureNameLabel(row) {
  if (!row || typeof row !== 'object') return row || '—'
  const refType = normalizeKey(row.refType)
  if (REF_TYPE_FEATURE_LABELS[refType]) return REF_TYPE_FEATURE_LABELS[refType]
  return row.featureName || row.serviceName || eventTypeLabel(row.eventType)
}

function modelLabel(row) {
  if (!row || typeof row !== 'object') return '—'
  return row.modelName || '—'
}

function defaultServiceNameForEvent(eventType) {
  if (eventType === 'AI_TEXT') return 'AI 텍스트'
  if (eventType === 'AI_IMAGE') return 'AI 이미지'
  if (eventType === 'DOCUMENT_OCR') return '문서 OCR'
  if (eventType === 'FAX') return '팩스'
  if (eventType === 'NOTIFICATION') return '알림톡'
  return eventTypeLabel(eventType)
}

function normalizeFormValue(value) {
  return String(value || '').trim()
}

function rateSourceKey(row) {
  return [
    row.eventType,
    row.provider,
    row.serviceName,
    row.unitType,
    row.modelName || '',
  ].map(normalizeFormValue).join('\u001F')
}

function addRateSource(map, row) {
  const source = {
    eventType: normalizeFormValue(row?.eventType),
    provider: normalizeFormValue(row?.provider),
    serviceName: normalizeFormValue(row?.serviceName),
    unitType: normalizeFormValue(row?.unitType),
    modelName: normalizeFormValue(row?.modelName),
  }
  if (!source.eventType || !source.provider || !source.serviceName || !source.unitType) return
  map.set(rateSourceKey(source), source)
}

function buildRatePolicySources(ratePolicies, summary, events) {
  const map = new Map()
  RATE_POLICY_PRESETS.forEach((row) => addRateSource(map, row))
  ;(asCollection(ratePolicies)).forEach((row) => addRateSource(map, row))
  ;(asCollection(events)).forEach((row) => addRateSource(map, row))
  ;(asArray(summary?.byAiModel)).forEach((row) => addRateSource(map, {
    eventType: row.eventType,
    provider: row.provider,
    serviceName: defaultServiceNameForEvent(row.eventType),
    unitType: row.unitType,
    modelName: row.modelName === '모델 미확인' ? '' : row.modelName,
  }))
  ;(asArray(summary?.byService)).forEach((row) => {
    const eventType = normalizeFormValue(row.eventType)
    const serviceName = normalizeFormValue(row.serviceName)
    const unitType = normalizeFormValue(row.unitType)
    RATE_POLICY_PRESETS
      .filter((preset) => preset.eventType === eventType && preset.serviceName === serviceName && preset.unitType === unitType)
      .forEach((preset) => addRateSource(map, preset))
  })
  return Array.from(map.values())
}

function sourceMatches(source, filters) {
  return Object.entries(filters || {}).every(([field, value]) => {
    const filterValue = normalizeFormValue(value)
    if (!filterValue) return true
    const sourceValue = normalizeFormValue(source?.[field])
    return !sourceValue || sourceValue === filterValue
  })
}

function collectRateOptions(sources, field, filters, currentValue = '') {
  const values = new Set()
  ;(sources || []).forEach((source) => {
    if (sourceMatches(source, filters)) {
      const value = normalizeFormValue(source?.[field])
      if (value) values.add(value)
    }
  })
  const current = normalizeFormValue(currentValue)
  if (current) values.add(current)
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b, 'ko-KR'))
    .map((value) => ({ value, label: value }))
}

function firstRateSource(sources, filters) {
  return (sources || []).find((source) => sourceMatches(source, filters))
}

function modelSpecificEvent(eventType) {
  return ['AI_TEXT', 'AI_IMAGE', 'DOCUMENT_OCR'].includes(eventType)
}

function ratePolicyPayload(form) {
  return {
    eventType: normalizeFormValue(form.eventType),
    provider: normalizeFormValue(form.provider),
    serviceName: normalizeFormValue(form.serviceName),
    modelName: normalizeFormValue(form.modelName) || null,
    unitType: normalizeFormValue(form.unitType),
    unitPrice: Number(form.unitPrice || 0),
    currency: normalizeFormValue(form.currency) || 'KRW',
    memo: normalizeFormValue(form.memo) || null,
    active: form.active !== false,
  }
}

function rateFormFromPolicy(row) {
  return {
    eventType: row.eventType || EMPTY_RATE_FORM.eventType,
    provider: row.provider || EMPTY_RATE_FORM.provider,
    serviceName: row.serviceName || EMPTY_RATE_FORM.serviceName,
    unitType: row.unitType || EMPTY_RATE_FORM.unitType,
    unitPrice: row.unitPrice ?? '',
    currency: row.currency || 'KRW',
    modelName: row.modelName || '',
    memo: row.memo || '',
    active: row.active !== false,
  }
}

function buildPeriodOptions() {
  const now = new Date()
  const base = [
    { value: 'today', label: '오늘' },
    { value: '7d', label: '최근 7일' },
    { value: '30d', label: '최근 30일' },
    { value: 'this-month', label: '이번 달' },
    { value: 'last-month', label: '지난 달' },
  ]
  const months = []
  for (let i = 0; i < 18; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    months.push({ value: `month:${ym}`, label: `${d.getFullYear()}년 ${d.getMonth() + 1}월` })
  }
  return [...base, ...months]
}

function periodParams(periodValue) {
  if (periodValue?.startsWith('month:')) {
    return { period: 'month', month: periodValue.slice('month:'.length) }
  }
  return { period: periodValue || 'today' }
}

function buildCategoryTrendRows(rows) {
  const byDate = new Map()
  ;(rows || []).forEach((row) => {
    if (!row?.statDate) return
    const key = row.statDate
    const category = featureCategoryLabel(row.featureCategory)
    const current = byDate.get(key) || { statDate: key }
    current[category] = Number(current[category] || 0) + Number(row.estimatedAmount || 0)
    byDate.set(key, current)
  })
  return Array.from(byDate.values()).sort((a, b) => String(a.statDate).localeCompare(String(b.statDate)))
}

function summarizeEventCategories(items) {
  const byCategory = new Map()
  ;(items || []).forEach((row) => {
    const category = featureCategoryLabel(row)
    const current = byCategory.get(category) || { featureCategory: category, eventCount: 0, estimatedAmount: 0 }
    current.eventCount += 1
    current.estimatedAmount += Number(row.estimatedAmount || 0)
    byCategory.set(category, current)
  })
  return Array.from(byCategory.values()).sort((a, b) => Number(b.estimatedAmount || 0) - Number(a.estimatedAmount || 0))
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-80" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
      <Skeleton className="h-72" />
    </div>
  )
}

export default function OpsOperationalCostsPage() {
  const [period, setPeriod] = useState('this-month')
  const [eventType, setEventType] = useState('')
  const [featureCategory, setFeatureCategory] = useState('')
  const [provider, setProvider] = useState('')
  const [q, setQ] = useState('')
  const [unpricedOnly, setUnpricedOnly] = useState(false)
  const [page, setPage] = useState(0)
  const [summary, setSummary] = useState(null)
  const [events, setEvents] = useState(null)
  const [ratePolicies, setRatePolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [rateSaving, setRateSaving] = useState(false)
  const [editingRateId, setEditingRateId] = useState(null)
  const [deletingRateId, setDeletingRateId] = useState(null)
  const [rateForm, setRateForm] = useState(EMPTY_RATE_FORM)

  const periodOptions = useMemo(() => buildPeriodOptions(), [])
  const params = useMemo(() => periodParams(period), [period])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const eventParams = {
        ...params,
        eventType: eventType || undefined,
        featureCategory: featureCategory || undefined,
        provider: provider || undefined,
        q: q || undefined,
        unpricedOnly,
        page,
        size: 10,
      }
      const [summaryRes, eventsRes, ratesRes] = await Promise.all([
        api.get('/ops/costs/summary', { params }),
        api.get('/ops/costs/events', { params: eventParams }),
        api.get('/ops/costs/rate-policies', { params: { activeOnly: false } }),
      ])
      setSummary(unwrapData(summaryRes))
      setEvents(unwrapData(eventsRes))
      setRatePolicies(asCollection(unwrapData(ratesRes)))
    } catch (e) {
      setErr(getApiErrorMessage(e, '운영비용 정보를 불러오지 못했습니다.'))
      setSummary(null)
      setEvents(null)
    } finally {
      setLoading(false)
    }
  }, [eventType, featureCategory, page, params, provider, q, unpricedOnly])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setPage(0)
  }, [period, eventType, featureCategory, provider, q, unpricedOnly])

  const saveRatePolicy = async (e) => {
    e.preventDefault()
    setRateSaving(true)
    setErr('')
    try {
      const payload = ratePolicyPayload(rateForm)
      if (editingRateId) {
        await api.patch(`/ops/costs/rate-policies/${editingRateId}`, payload)
      } else {
        await api.post('/ops/costs/rate-policies', payload)
      }
      setEditingRateId(null)
      setRateForm(EMPTY_RATE_FORM)
      await load()
    } catch (ex) {
      setErr(getApiErrorMessage(ex, '단가 정책을 저장하지 못했습니다.'))
    } finally {
      setRateSaving(false)
    }
  }

  const startEditRatePolicy = (row) => {
    setEditingRateId(row.id)
    setRateForm(rateFormFromPolicy(row))
  }

  const cancelEditRatePolicy = () => {
    setEditingRateId(null)
    setRateForm(EMPTY_RATE_FORM)
  }

  const deleteRatePolicy = async (row) => {
    if (!row?.id) return
    if (!window.confirm('이 단가 정책을 삭제하시겠습니까? 이미 기록된 비용 이력은 유지됩니다.')) return
    setDeletingRateId(row.id)
    setErr('')
    try {
      await api.delete(`/ops/costs/rate-policies/${row.id}`)
      if (editingRateId === row.id) {
        setEditingRateId(null)
        setRateForm(EMPTY_RATE_FORM)
      }
      await load()
    } catch (ex) {
      setErr(getApiErrorMessage(ex, '단가 정책을 삭제하지 못했습니다.'))
    } finally {
      setDeletingRateId(null)
    }
  }

  const categoryOptions = useMemo(() => {
    const names = new Map()
    STATIC_FEATURE_CATEGORIES.forEach((name) => names.set(name, name))
    ;(asArray(summary?.byFeatureCategory)).forEach((row) => {
      const label = featureCategoryLabel(row.featureCategory)
      names.set(label, label)
    })
    ;(asCollection(events)).forEach((row) => {
      const label = featureCategoryLabel(row)
      names.set(label, label)
    })
    if (featureCategory) {
      names.set(featureCategory, featureCategory)
    }
    return [
      { value: '', label: '전체 카테고리' },
      ...Array.from(names.keys()).map((name) => ({ value: name, label: name })),
    ]
  }, [events, featureCategory, summary])

  const ratePolicySources = useMemo(
    () => buildRatePolicySources(ratePolicies, summary, events),
    [events, ratePolicies, summary],
  )

  const rateProviderOptions = useMemo(
    () => collectRateOptions(ratePolicySources, 'provider', { eventType: rateForm.eventType }, rateForm.provider),
    [rateForm.eventType, rateForm.provider, ratePolicySources],
  )

  const rateServiceOptions = useMemo(
    () => collectRateOptions(
      ratePolicySources,
      'serviceName',
      { eventType: rateForm.eventType, provider: rateForm.provider },
      rateForm.serviceName,
    ),
    [rateForm.eventType, rateForm.provider, rateForm.serviceName, ratePolicySources],
  )

  const rateUnitOptions = useMemo(
    () => collectRateOptions(
      ratePolicySources,
      'unitType',
      { eventType: rateForm.eventType, provider: rateForm.provider, serviceName: rateForm.serviceName },
      rateForm.unitType,
    ),
    [rateForm.eventType, rateForm.provider, rateForm.serviceName, rateForm.unitType, ratePolicySources],
  )

  const rateModelOptions = useMemo(
    () => collectRateOptions(
      ratePolicySources,
      'modelName',
      {
        eventType: rateForm.eventType,
        provider: rateForm.provider,
        serviceName: rateForm.serviceName,
        unitType: rateForm.unitType,
      },
      rateForm.modelName,
    ),
    [rateForm.eventType, rateForm.modelName, rateForm.provider, rateForm.serviceName, rateForm.unitType, ratePolicySources],
  )

  const updateRateFormField = (field, value) => {
    setRateForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'eventType') {
        const source = firstRateSource(ratePolicySources, { eventType: value })
        next.provider = source?.provider || ''
        next.serviceName = source?.serviceName || defaultServiceNameForEvent(value)
        next.unitType = source?.unitType || next.unitType
        next.modelName = modelSpecificEvent(value) ? (source?.modelName || '') : ''
      } else if (field === 'provider') {
        const source = firstRateSource(ratePolicySources, { eventType: next.eventType, provider: value })
        next.serviceName = source?.serviceName || next.serviceName
        next.unitType = source?.unitType || next.unitType
        next.modelName = modelSpecificEvent(next.eventType) ? (source?.modelName || '') : ''
      } else if (field === 'serviceName') {
        const source = firstRateSource(ratePolicySources, {
          eventType: next.eventType,
          provider: next.provider,
          serviceName: value,
        })
        next.unitType = source?.unitType || next.unitType
        next.modelName = modelSpecificEvent(next.eventType) ? (source?.modelName || '') : ''
      } else if (field === 'unitType') {
        const source = firstRateSource(ratePolicySources, {
          eventType: next.eventType,
          provider: next.provider,
          serviceName: next.serviceName,
          unitType: value,
        })
        next.modelName = modelSpecificEvent(next.eventType) ? (source?.modelName || next.modelName) : ''
      }
      if (!modelSpecificEvent(next.eventType)) {
        next.modelName = ''
      }
      return next
    })
  }

  const topCategoryNames = useMemo(() => (
    (asArray(summary?.byFeatureCategory))
      .slice(0, 6)
      .map((row) => featureCategoryLabel(row.featureCategory))
  ), [summary])

  const categoryTrendRows = useMemo(
    () => buildCategoryTrendRows(asArray(summary?.categoryDailyTrend)),
    [summary],
  )

  const eventCategoryFallback = useMemo(
    () => summarizeEventCategories(asCollection(events)),
    [events],
  )

  if (loading && !summary) {
    return (
      <div className="sgez-shadcn-dashboard bg-background text-foreground">
        <DashboardSkeleton />
      </div>
    )
  }

  const totals = summary?.totals || {}
  const dailyTrend = asArray(summary?.dailyTrend)
  const byProvider = asArray(summary?.byProvider)
  const byAiModel = asArray(summary?.byAiModel)
  const byFeatureCategory = asArray(summary?.byFeatureCategory)
  const topFeatures = asArray(summary?.topFeatures)
  const eventItems = asCollection(events)
  const leadingCategory = byFeatureCategory[0] || eventCategoryFallback[0]
  const periodLabel = summary?.period?.label || periodOptions.find((o) => o.value === period)?.label || '선택 기간'

  return (
    <div className="sgez-shadcn-dashboard space-y-8 bg-background text-foreground">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">운영관리 · 운영비용 대시보드</h1>
            <Badge variant="outline" className="font-normal">KST 기준</Badge>
          </div>
          {summary?.generatedAt ? (
            <p className="mt-2 text-xs text-muted-foreground">생성 시각: {formatDateTime(summary.generatedAt)}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 min-w-[12rem] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="h-4 w-4" />
            새로고침
          </Button>
        </div>
      </div>

      {err ? (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">확인 필요</CardTitle>
            <CardDescription>{err}</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{periodLabel}</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{money(totals.estimatedAmount)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">예상 비용</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>정산 확정</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{money(totals.actualAmount)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">공급사 대사 반영분</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>발생 이벤트</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{num(totals.eventCount)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">원장 기준</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>주요 카테고리</CardDescription>
            <CardTitle className="truncate text-xl">
              {leadingCategory ? featureCategoryLabel(leadingCategory.featureCategory) : '데이터 없음'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            {leadingCategory ? `${money(leadingCategory.estimatedAmount)} · ${num(leadingCategory.eventCount)}건` : '기간 내 데이터 없음'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>단가 미설정</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{num(totals.unpricedCount)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">비용 산정 대기</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>실패 이벤트</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{num(totals.failedCount)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">실패/오류 상태</CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(20rem,0.6fr)]">
        <Card>
          <CardHeader>
            <CardTitle>일별 비용 추이</CardTitle>
            <CardDescription>선택 기간의 예상 비용과 단가 미설정 이벤트 수</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              {dailyTrend.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">데이터 없음</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d4d4d8" opacity={0.85} />
                    <XAxis dataKey="statDate" tick={{ fontSize: 11 }} minTickGap={20} />
                    <YAxis yAxisId="cost" tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => `${Math.round(Number(v || 0) / 1000)}천`} />
                    <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11 }} width={42} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      formatter={(value, name) => {
                        if (name === 'estimatedAmount') return [money(value), '예상 비용']
                        if (name === 'unpricedCount') return [num(value), '단가 미설정']
                        return [num(value), name]
                      }}
                    />
                    <Line yAxisId="cost" type="monotone" dataKey="estimatedAmount" stroke="#0d9488" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line yAxisId="count" type="monotone" dataKey="unpricedCount" stroke="#f97316" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>카테고리별 비용 추이</CardTitle>
            <CardDescription>업무 카테고리 기준 예상 비용</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              {categoryTrendRows.length === 0 || topCategoryNames.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">데이터 없음</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={categoryTrendRows} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d4d4d8" opacity={0.85} />
                    <XAxis dataKey="statDate" tick={{ fontSize: 11 }} minTickGap={20} />
                    <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => `${Math.round(Number(v || 0) / 1000)}천`} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      formatter={(value, name) => [money(value), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {topCategoryNames.map((name, index) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>카테고리·기능별 비용</CardTitle>
          <CardDescription>비용이 발생한 업무 기능 기준 집계</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 xl:grid-cols-[minmax(14rem,0.8fr)_minmax(0,1.35fr)_minmax(14rem,0.75fr)]">
            <section>
              <h3 className="mb-2 text-sm font-semibold">카테고리별</h3>
              <ul className="divide-y rounded-lg border text-sm">
                {byFeatureCategory.length === 0 ? <li className="px-3 py-3 text-muted-foreground">데이터 없음</li> : null}
                {byFeatureCategory.map((row) => (
                  <li key={featureCategoryLabel(row.featureCategory)} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="min-w-0">
                      <p className="font-medium">{featureCategoryLabel(row.featureCategory)}</p>
                      <p className="text-xs text-muted-foreground">{num(row.eventCount)}건 · {num(row.billableQuantity)}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold tabular-nums">{money(row.estimatedAmount)}</p>
                      {Number(row.unpricedCount || 0) > 0 ? (
                        <p className="text-xs text-amber-700">단가 미설정 {num(row.unpricedCount)}건</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">기능별</h3>
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>카테고리</TableHead>
                      <TableHead>기능</TableHead>
                      <TableHead className="text-right">건수</TableHead>
                      <TableHead className="text-right">예상 비용</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topFeatures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-muted-foreground">데이터 없음</TableCell>
                      </TableRow>
                    ) : null}
                    {topFeatures.map((row) => (
                      <TableRow key={`${featureCategoryLabel(row)}-${row.featureName}-${row.eventType}`}>
                        <TableCell className="whitespace-nowrap">{featureCategoryLabel(row)}</TableCell>
                        <TableCell>
                          <div className="min-w-[12rem]">
                            <p className="font-medium">{row.featureName || eventTypeLabel(row.eventType)}</p>
                            <p className="text-xs text-muted-foreground">{eventTypeLabel(row.eventType)} · {num(row.billableQuantity)}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{num(row.eventCount)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{money(row.estimatedAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">공급사별</h3>
              <ul className="divide-y rounded-lg border text-sm">
                {byProvider.length === 0 ? <li className="px-3 py-3 text-muted-foreground">데이터 없음</li> : null}
                {byProvider.map((row) => (
                  <li key={row.provider} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="min-w-0">
                      <p className="font-medium">{row.provider || '공급사 미확인'}</p>
                      <p className="text-xs text-muted-foreground">{num(row.eventCount)}건 · {num(row.billableQuantity)}</p>
                    </div>
                    <span className="shrink-0 font-semibold tabular-nums">{money(row.estimatedAmount)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI 모델별 비용</CardTitle>
          <CardDescription>공급사·모델 기준 토큰/이미지 비용 집계</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>공급사/모델</TableHead>
                <TableHead>구분</TableHead>
                <TableHead className="text-right">건수</TableHead>
                <TableHead className="text-right">수량</TableHead>
                <TableHead className="text-right">예상 비용</TableHead>
                <TableHead className="text-right">단가 미설정</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byAiModel.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">데이터 없음</TableCell>
                </TableRow>
              ) : null}
              {byAiModel.map((row) => (
                <TableRow key={`${row.provider}-${row.modelName}-${row.eventType}-${row.unitType}`}>
                  <TableCell>
                    <div className="min-w-[14rem]">
                      <p className="font-medium">{row.provider || '공급사 미확인'}</p>
                      <p className="text-xs text-muted-foreground">{row.modelName || '모델 미확인'}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[8rem]">
                      <p>{eventTypeLabel(row.eventType)}</p>
                      <p className="text-xs text-muted-foreground">{unitLabel(row.unitType)}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{num(row.eventCount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(row.billableQuantity)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(row.estimatedAmount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(row.unpricedCount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-4">
          <div>
            <CardTitle>비용 이력</CardTitle>
            <CardDescription>원장 이벤트 기준 상세 내역</CardDescription>
          </div>
          <div className="grid gap-2 md:grid-cols-[11rem_12rem_10rem_minmax(12rem,1fr)_auto]">
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            >
              {EVENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={featureCategory}
              onChange={(e) => setFeatureCategory(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            >
              {categoryOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>{option.label}</option>
              ))}
            </select>
            <input
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="공급사"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="기능명, 접수번호"
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            />
            <label className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
              <input type="checkbox" checked={unpricedOnly} onChange={(e) => setUnpricedOnly(e.target.checked)} />
              단가 미설정
            </label>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[10rem]">발생시각</TableHead>
                <TableHead>카테고리</TableHead>
                <TableHead>기능</TableHead>
                <TableHead>서비스/공급사</TableHead>
                <TableHead>모델</TableHead>
                <TableHead>상태</TableHead>
                <TableHead className="text-right">수량</TableHead>
                <TableHead className="text-right">단가</TableHead>
                <TableHead className="text-right">예상 비용</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-muted-foreground">데이터 없음</TableCell>
                </TableRow>
              ) : null}
              {eventItems.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-xs">{formatDateTime(row.occurredAt)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="whitespace-nowrap font-normal">
                      {featureCategoryLabel(row)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[13rem]">
                      <p className="font-medium">{featureNameLabel(row)}</p>
                      <p className="text-xs text-muted-foreground">{eventTypeLabel(row.eventType)}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[8rem]">
                      <p>{row.serviceName || '—'}</p>
                      <p className="text-xs text-muted-foreground">{row.provider || '—'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="min-w-[10rem]">{modelLabel(row)}</div>
                  </TableCell>
                  <TableCell>{statusLabel(row.status)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(row.billableQuantity)} {unitLabel(row.unitType)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.unitPrice == null ? '—' : unitMoney(row.unitPrice, row.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(row.estimatedAmount, row.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>{num(events?.totalElements || 0)}건</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage((v) => Math.max(0, v - 1))}>이전</Button>
              <Button variant="outline" size="sm" disabled={!events || page + 1 >= Number(events.totalPages || 0)} onClick={() => setPage((v) => v + 1)}>다음</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>단가 정책</CardTitle>
          <CardDescription>구분·공급사·서비스·모델·과금 단위별 적용 단가</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form onSubmit={saveRatePolicy} className="grid gap-2 md:grid-cols-4 xl:grid-cols-10">
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.eventType} onChange={(e) => updateRateFormField('eventType', e.target.value)}>
              {EVENT_TYPE_OPTIONS.filter((o) => o.value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.provider} onChange={(e) => updateRateFormField('provider', e.target.value)} required>
              {rateProviderOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.serviceName} onChange={(e) => updateRateFormField('serviceName', e.target.value)} required>
              {rateServiceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.modelName} onChange={(e) => updateRateFormField('modelName', e.target.value)} disabled={!modelSpecificEvent(rateForm.eventType)}>
              <option value="">전체 모델</option>
              {rateModelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.unitType} onChange={(e) => updateRateFormField('unitType', e.target.value)}>
              {(rateUnitOptions.length > 0 ? rateUnitOptions : UNIT_OPTIONS).map((o) => <option key={o.value} value={o.value}>{unitLabel(o.value)}</option>)}
            </select>
            <input className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.unitPrice} onChange={(e) => setRateForm((p) => ({ ...p, unitPrice: e.target.value }))} placeholder="단가" type="number" step="0.000001" min="0" required />
            <input className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={rateForm.memo} onChange={(e) => setRateForm((p) => ({ ...p, memo: e.target.value }))} placeholder="메모" />
            <label className="inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm">
              <input type="checkbox" checked={rateForm.active !== false} onChange={(e) => setRateForm((p) => ({ ...p, active: e.target.checked }))} />
              사용
            </label>
            <Button type="submit" size="sm" disabled={rateSaving}>
              <Check className="h-4 w-4" />
              {rateSaving ? '저장 중' : editingRateId ? '수정 저장' : '단가 추가'}
            </Button>
            {editingRateId ? (
              <Button type="button" variant="outline" size="sm" onClick={cancelEditRatePolicy} disabled={rateSaving}>
                <X className="h-4 w-4" />
                취소
              </Button>
            ) : null}
          </form>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>구분</TableHead>
                  <TableHead>공급사</TableHead>
                  <TableHead>서비스</TableHead>
                  <TableHead>모델</TableHead>
                  <TableHead>단위</TableHead>
                  <TableHead className="text-right">단가</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ratePolicies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground">등록된 단가 정책 없음</TableCell>
                  </TableRow>
                ) : null}
                {ratePolicies.map((row) => (
                  <TableRow key={row.id} className={editingRateId === row.id ? 'bg-muted/40' : undefined}>
                    <TableCell>{eventTypeLabel(row.eventType)}</TableCell>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell>{row.serviceName}</TableCell>
                    <TableCell>{row.modelName || '전체'}</TableCell>
                    <TableCell>{unitLabel(row.unitType)}</TableCell>
                    <TableCell className="text-right tabular-nums">{unitMoney(row.unitPrice, row.currency)}</TableCell>
                    <TableCell>{row.active ? '사용' : '중지'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => startEditRatePolicy(row)} disabled={rateSaving || deletingRateId === row.id} aria-label="단가 정책 수정" title="수정">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => deleteRatePolicy(row)} disabled={rateSaving || deletingRateId === row.id} className="text-destructive hover:text-destructive" aria-label="단가 정책 삭제" title="삭제">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
